import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { X, Plus, Trash2, Star, AlertCircle, Upload, Download, ArrowUp, ArrowDown, Type, Image as ImageIcon, Eye, EyeOff } from 'lucide-react'
import { TOKENS } from './templates'
import { renderThumb, canvasToBlob, uploadThumb, absUrl } from './thumbRender'

// Thumbnail template editor. Left: templates for the league. Right: the plate
// with layers painted for a real game — drag a layer to move it, arrow keys
// nudge, fields below edit the selected layer precisely. Positions are in
// template pixels (1920x1080) no matter how the preview is scaled.
//
// props: league, templates[], sampleGames[], ctxFor(g), sourcesFor(g), api ({ apiRaw, ...sched }),
//        onChange(templates), onClose

const PREVIEW_W = 880
const newId = (p) => `${p}-${Math.random().toString(36).slice(2, 7)}`
const TEXT_DEFAULT = { type: 'text', text: '<away-team>', x: 100, y: 200, w: 600, size: 44, weight: 700, font: 'Open Sans', color: '#ffffff', align: 'left' }
const IMAGE_DEFAULT = { type: 'image', source: 'away-logo', x: 100, y: 300, w: 300, h: 300 }

// Hit box for a layer (template px). Text boxes are approximated from size.
function boxOf(l) {
  if (l.type === 'image') return { x: l.x, y: l.y, w: l.w, h: l.h }
  const size = l.size || 40
  return { x: l.x, y: l.y - size, w: l.w || 400, h: size * 1.3 }
}

export default function ThumbTemplates({ league, templates, sampleGames, ctxFor, sourcesFor, api, onChange, onClose }) {
  const [activeId, setActiveId] = useState(() => (templates.find(t => t.is_default) || templates[0])?.id ?? 'new')
  const [draft, setDraft] = useState(null)         // { name, background_url, width, height, layers }
  const [sel, setSel] = useState(null)             // selected layer id
  const [sampleId, setSampleId] = useState(sampleGames[0]?.game_id || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const canvasRef = useRef(null)
  const fileRef = useRef(null)
  const drag = useRef(null)

  const active = useMemo(() => templates.find(t => t.id === activeId) || null, [templates, activeId])
  useEffect(() => {
    setDraft(active
      ? { name: active.name, background_url: active.background_url, width: active.width || 1920, height: active.height || 1080, layers: active.layers.map(l => ({ ...l })) }
      : { name: '', background_url: '', width: 1920, height: 1080, layers: [] })
    setSel(null); setErr('')
  }, [active])

  useEffect(() => {
    if (!sampleGames.find(g => g.game_id === sampleId)) setSampleId(sampleGames[0]?.game_id || '')
  }, [sampleGames, sampleId])

  const sample = sampleGames.find(g => g.game_id === sampleId) || null
  const scale = draft ? PREVIEW_W / (draft.width || 1920) : 1
  const dirty = !!draft && (!active
    ? !!(draft.name || draft.background_url || draft.layers.length)
    : draft.name !== active.name || draft.background_url !== active.background_url || JSON.stringify(draft.layers) !== JSON.stringify(active.layers))

  // ---- paint ----
  const paint = useCallback(async () => {
    const c = canvasRef.current
    if (!c || !draft) return
    const game = sample
    const data = game ? { ctx: ctxFor(game), sources: sourcesFor(game) } : { ctx: {}, sources: {} }
    await renderThumb(draft, data, { canvas: c, scale })
    // selection outline
    if (sel) {
      const l = draft.layers.find(x => x.id === sel)
      if (l) {
        const g = c.getContext('2d')
        const b = boxOf(l)
        g.setTransform(scale, 0, 0, scale, 0, 0)
        g.strokeStyle = '#e94560'; g.lineWidth = 3 / scale; g.setLineDash([8 / scale, 6 / scale])
        g.strokeRect(b.x, b.y, b.w, b.h)
        g.setLineDash([])
      }
    }
  }, [draft, sample, sel, scale, ctxFor, sourcesFor])
  useEffect(() => { paint() }, [paint])

  // ---- drag / nudge ----
  // Pointer → template pixels: fraction of the rendered box × template size.
  function ptFromEvent(e) {
    const r = canvasRef.current.getBoundingClientRect()
    return { x: (e.clientX - r.left) / r.width * draft.width, y: (e.clientY - r.top) / r.height * draft.height }
  }
  function onDown(e) {
    if (!draft) return
    const p = ptFromEvent(e)
    const hit = [...draft.layers].reverse().find(l => !l.hidden && (() => { const b = boxOf(l); return p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h })())
    setSel(hit?.id || null)
    if (hit) { drag.current = { id: hit.id, sx: p.x, sy: p.y, ox: hit.x, oy: hit.y }; e.currentTarget.setPointerCapture?.(e.pointerId) }
  }
  function onMove(e) {
    if (!drag.current) return
    const p = ptFromEvent(e)
    const { id, sx, sy, ox, oy } = drag.current
    updateLayer(id, { x: Math.round(ox + (p.x - sx)), y: Math.round(oy + (p.y - sy)) })
  }
  function onUp() { drag.current = null }
  function onKey(e) {
    if (!sel) return
    const step = e.shiftKey ? 10 : 1
    const l = draft.layers.find(x => x.id === sel)
    if (!l) return
    if (e.key === 'ArrowLeft') updateLayer(sel, { x: l.x - step })
    else if (e.key === 'ArrowRight') updateLayer(sel, { x: l.x + step })
    else if (e.key === 'ArrowUp') updateLayer(sel, { y: l.y - step })
    else if (e.key === 'ArrowDown') updateLayer(sel, { y: l.y + step })
    else return
    e.preventDefault()
  }

  // ---- layer edits ----
  const updateLayer = (id, patch) => setDraft(d => ({ ...d, layers: d.layers.map(l => (l.id === id ? { ...l, ...patch } : l)) }))
  const addLayer = (kind) => {
    const l = { id: newId(kind), ...(kind === 'text' ? TEXT_DEFAULT : IMAGE_DEFAULT) }
    setDraft(d => ({ ...d, layers: [...d.layers, l] })); setSel(l.id)
  }
  const removeLayer = (id) => { setDraft(d => ({ ...d, layers: d.layers.filter(l => l.id !== id) })); setSel(null) }
  const moveLayer = (id, dir) => setDraft(d => {
    const i = d.layers.findIndex(l => l.id === id); const j = i + dir
    if (i < 0 || j < 0 || j >= d.layers.length) return d
    const ls = [...d.layers]; [ls[i], ls[j]] = [ls[j], ls[i]]; return { ...d, layers: ls }
  })
  const selected = draft?.layers.find(l => l.id === sel) || null

  // ---- background upload ----
  async function uploadBackground(file) {
    setBusy(true); setErr('')
    try {
      const { url } = await uploadThumb(api.apiRaw, file, file.name)
      setDraft(d => ({ ...d, background_url: url }))
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  // ---- save / delete / test ----
  async function save(asDefault = false) {
    if (!draft.name.trim() || !draft.background_url) { setErr('Name and a background image are required'); return }
    setBusy(true); setErr('')
    try {
      const body = { name: draft.name.trim(), background_url: draft.background_url, width: draft.width, height: draft.height, layers: draft.layers, ...(asDefault ? { is_default: true } : {}) }
      let saved
      if (active) ({ template: saved } = await api.updateThumbTemplate(active.id, body))
      else ({ template: saved } = await api.createThumbTemplate({ league, ...body, is_default: asDefault || templates.length === 0 }))
      const { templates: list } = await api.listThumbTemplates(league)
      onChange(list); setActiveId(saved.id)
    } catch (e) { setErr(e.body?.error || e.message) } finally { setBusy(false) }
  }
  async function remove() {
    if (!active || !window.confirm(`Delete thumbnail template "${active.name}"?`)) return
    setBusy(true)
    try {
      await api.deleteThumbTemplate(active.id)
      const { templates: list } = await api.listThumbTemplates(league)
      onChange(list); setActiveId((list.find(t => t.is_default) || list[0])?.id ?? 'new')
    } catch (e) { setErr(e.body?.error || e.message) } finally { setBusy(false) }
  }
  async function downloadTest() {
    if (!sample) return
    setBusy(true)
    try {
      const c = await renderThumb(draft, { ctx: ctxFor(sample), sources: sourcesFor(sample) })
      const blob = await canvasToBlob(c)
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob); a.download = `thumb-test-${sample.game_date}.jpg`; a.click()
      setTimeout(() => URL.revokeObjectURL(a.href), 5000)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  if (!draft) return null
  return (
    <div className="ts-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="ts-modal ts-modal-xwide" role="dialog" aria-modal="true" aria-labelledby="ts-thumb-title">
        <div className="ts-modal-head">
          <h2 id="ts-thumb-title">Thumbnail templates <span className="muted">· {league}</span></h2>
          <button type="button" className="ts-iconbtn" onClick={onClose} aria-label="Close" disabled={busy}><X size={16} /></button>
        </div>

        <div className="ts-tpl-body ts-thumb-body">
          <aside className="ts-tpl-list" aria-label="Thumbnail templates">
            {templates.map(t => (
              <button key={t.id} type="button" className={`ts-tpl-item ${t.id === activeId ? 'is-active' : ''}`} onClick={() => setActiveId(t.id)}>
                <span>{t.name}</span>
                {t.is_default ? <span className="ts-tpl-default"><Star size={11} /> default</span> : null}
              </button>
            ))}
            <button type="button" className={`ts-tpl-item ts-tpl-new ${activeId === 'new' ? 'is-active' : ''}`} onClick={() => setActiveId('new')}><Plus size={13} /> New template</button>
          </aside>

          <div className="ts-thumb-editor">
            {/* name + background */}
            <div className="ts-sch-grid">
              <div className="form-group">
                <label htmlFor="ts-thumb-name">Template name</label>
                <input id="ts-thumb-name" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="High School Hockey Night 2026-27" />
              </div>
              <div className="form-group ts-span-2">
                <label htmlFor="ts-thumb-bg">Background plate <span className="muted">({draft.width}×{draft.height}, exported from Canva with the variable elements removed)</span></label>
                <div className="ts-thumb-bgrow">
                  <input id="ts-thumb-bg" type="url" inputMode="url" value={draft.background_url} onChange={e => setDraft(d => ({ ...d, background_url: e.target.value }))} placeholder="https://imagedelivery.net/…/public" />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}><Upload size={14} /> Upload</button>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={e => { if (e.target.files?.[0]) uploadBackground(e.target.files[0]); e.target.value = '' }} />
                </div>
              </div>
            </div>

            {/* preview */}
            <div className="ts-thumb-previewbar">
              <span className="muted">Preview with</span>
              <select value={sampleId} onChange={e => setSampleId(e.target.value)} aria-label="Preview game" disabled={!sampleGames.length}>
                {!sampleGames.length && <option value="">No games synced yet</option>}
                {sampleGames.map(g => <option key={g.game_id} value={g.game_id}>{g.game_date} · {g.visitor} at {g.home}</option>)}
              </select>
              <span className="ts-spacer" />
              <span className="muted">Drag a layer to move · arrow keys nudge (⇧ = 10 px)</span>
              <button type="button" onClick={downloadTest} disabled={busy || !sample} title="Download a full-size 1920×1080 JPEG of this preview"><Download size={14} /> Test image</button>
            </div>
            <canvas
              ref={canvasRef} className="ts-thumb-canvas" tabIndex={0} aria-label="Template preview"
              style={{ width: PREVIEW_W, aspectRatio: `${draft.width} / ${draft.height}` }}
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onKeyDown={onKey}
            />

            {/* layers */}
            <div className="ts-thumb-layers">
              <div className="ts-thumb-layerlist">
                <div className="ts-thumb-layerhead">
                  <span>Layers</span>
                  <span className="ts-spacer" />
                  <button type="button" className="ts-chip" onClick={() => addLayer('text')}><Type size={12} /> Text</button>
                  <button type="button" className="ts-chip" onClick={() => addLayer('image')}><ImageIcon size={12} /> Image</button>
                </div>
                {draft.layers.map(l => (
                  <button key={l.id} type="button" className={`ts-tpl-item ts-thumb-layer ${sel === l.id ? 'is-active' : ''} ${l.hidden ? 'is-hidden' : ''}`} onClick={() => setSel(l.id)}>
                    {l.type === 'text' ? <Type size={12} /> : <ImageIcon size={12} />}
                    <span className="ts-thumb-layername">{l.type === 'text' ? (l.text || '(empty)') : (l.source || '(no source)')}</span>
                    <span className="muted mono">{l.x},{l.y}</span>
                  </button>
                ))}
                {!draft.layers.length && <p className="muted">No layers yet — add a text or image layer.</p>}
              </div>

              <div className="ts-thumb-props">
                {!selected && <p className="muted">Select a layer to edit it.</p>}
                {selected && (
                  <>
                    <div className="ts-thumb-propsrow">
                      <strong>{selected.type === 'text' ? 'Text layer' : 'Image layer'}</strong>
                      <span className="ts-spacer" />
                      <button type="button" className="ts-iconbtn ts-iconbtn-xs" onClick={() => moveLayer(selected.id, -1)} title="Paint earlier (send back)"><ArrowUp size={13} /></button>
                      <button type="button" className="ts-iconbtn ts-iconbtn-xs" onClick={() => moveLayer(selected.id, 1)} title="Paint later (bring forward)"><ArrowDown size={13} /></button>
                      <button type="button" className="ts-iconbtn ts-iconbtn-xs" onClick={() => updateLayer(selected.id, { hidden: !selected.hidden })} title={selected.hidden ? 'Show' : 'Hide'}>{selected.hidden ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                      <button type="button" className="ts-iconbtn ts-iconbtn-xs" onClick={() => removeLayer(selected.id)} title="Delete layer"><Trash2 size={13} /></button>
                    </div>

                    {selected.type === 'text' ? (
                      <>
                        <div className="form-group">
                          <label>Text <span className="muted">(tokens allowed)</span></label>
                          <input value={selected.text || ''} onChange={e => updateLayer(selected.id, { text: e.target.value })} />
                          <div className="ts-tpl-tokens ts-thumb-tokens">
                            {TOKENS.map(t => <button key={t.key} type="button" className="ts-chip ts-token" onClick={() => updateLayer(selected.id, { text: `${selected.text || ''}<${t.key}>` })} title={`<${t.key}>`}>{t.label}</button>)}
                          </div>
                        </div>
                        <div className="ts-thumb-grid4">
                          <Num label="X" v={selected.x} on={v => updateLayer(selected.id, { x: v })} />
                          <Num label="Baseline Y" v={selected.y} on={v => updateLayer(selected.id, { y: v })} />
                          <Num label="Max width" v={selected.w} on={v => updateLayer(selected.id, { w: v })} />
                          <Num label="Size" v={selected.size} on={v => updateLayer(selected.id, { size: v })} />
                          <div className="form-group"><label>Weight</label>
                            <select value={selected.weight || 700} onChange={e => updateLayer(selected.id, { weight: Number(e.target.value) })}>
                              {[400, 600, 700, 800].map(w => <option key={w} value={w}>{w}</option>)}
                            </select>
                          </div>
                          <div className="form-group"><label>Font</label><input value={selected.font || 'Open Sans'} onChange={e => updateLayer(selected.id, { font: e.target.value })} /></div>
                          <div className="form-group"><label>Align</label>
                            <select value={selected.align || 'left'} onChange={e => updateLayer(selected.id, { align: e.target.value })}>
                              <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                            </select>
                          </div>
                          <div className="form-group"><label>Color</label>
                            <div className="ts-thumb-color">
                              <select value={['away-color', 'home-color'].includes(selected.color) ? selected.color : 'custom'} onChange={e => updateLayer(selected.id, { color: e.target.value === 'custom' ? '#ffffff' : e.target.value })}>
                                <option value="custom">Fixed</option><option value="away-color">Away team color</option><option value="home-color">Home team color</option>
                              </select>
                              {['away-color', 'home-color'].includes(selected.color)
                                ? <input type="color" value={selected.fallbackColor || '#111111'} onChange={e => updateLayer(selected.id, { fallbackColor: e.target.value })} title="Fallback when the team has no color" />
                                : <input type="color" value={selected.color || '#ffffff'} onChange={e => updateLayer(selected.id, { color: e.target.value })} />}
                            </div>
                          </div>
                        </div>
                        <label className="ts-check"><input type="checkbox" checked={!!selected.uppercase} onChange={e => updateLayer(selected.id, { uppercase: e.target.checked })} /><span>Uppercase</span></label>
                      </>
                    ) : (
                      <>
                        <div className="form-group"><label>Source</label>
                          <select value={['away-logo', 'home-logo', 'league-logo'].includes(selected.source) ? selected.source : 'url'} onChange={e => updateLayer(selected.id, { source: e.target.value === 'url' ? '' : e.target.value })}>
                            <option value="away-logo">Away team logo</option><option value="home-logo">Home team logo</option><option value="league-logo">League logo</option><option value="url">Fixed image URL</option>
                          </select>
                          {!['away-logo', 'home-logo', 'league-logo'].includes(selected.source) && (
                            <input type="url" inputMode="url" value={selected.source || ''} onChange={e => updateLayer(selected.id, { source: e.target.value })} placeholder="https://…" />
                          )}
                        </div>
                        <div className="ts-thumb-grid4">
                          <Num label="X" v={selected.x} on={v => updateLayer(selected.id, { x: v })} />
                          <Num label="Y" v={selected.y} on={v => updateLayer(selected.id, { y: v })} />
                          <Num label="Width" v={selected.w} on={v => updateLayer(selected.id, { w: v })} />
                          <Num label="Height" v={selected.h} on={v => updateLayer(selected.id, { h: v })} />
                        </div>
                        <p className="muted">Logos are contain-fit and centred inside the box.</p>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {err && <p className="ts-tpl-warn"><AlertCircle size={13} /> {err}</p>}

            <div className="ts-modal-actions">
              <button type="button" onClick={() => save(false)} disabled={busy || !dirty}>{busy ? 'Saving…' : active ? 'Save' : 'Create'}</button>
              {!active?.is_default && <button type="button" onClick={() => save(true)} disabled={busy}><Star size={13} /> {active ? 'Save as default' : 'Create as default'}</button>}
              {active && <button type="button" className="danger" onClick={remove} disabled={busy}><Trash2 size={13} /> Delete</button>}
              <span className="ts-spacer" />
              <button type="button" onClick={onClose} disabled={busy}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Num({ label, v, on }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <input type="number" inputMode="numeric" value={v ?? 0} onChange={e => on(Number(e.target.value) || 0)} />
    </div>
  )
}
